import React from "react"

import { differenceInHours, format } from "date-fns"
import { Calendar, Clock, Loader2, Play, Square } from "lucide-react"

import { PersonCombobox } from "@/components/common/PersonCombobox"
import RoleTeamGuard from "@/components/guards/RoleTeamGuard"
import ShiftAuditDrawer from "@/components/sheriff/shift/ShiftAuditDrawer"
import ShiftChangesButton from "@/components/sheriff/shift/ShiftChangesButton"
import { Button } from "@/components/ui/button"
import { Calendar as CalendarComponent } from "@/components/ui/calendar"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Textarea } from "@/components/ui/textarea"
import { useAuth } from "@/contexts/AuthContext"
import { useShiftChangesPanel } from "@/hooks/useShiftChangesPanel"
import { ukInstantOf } from "@/lib/ukCalendarDate"
import { cn } from "@/lib/utils"
import { CancelledReason } from "@/services/shiftsService"
import { Person } from "@/types/person"

export interface ShiftDetails {
  id: string
  name: string
  personId?: string // Add person ID for editing
  timesheetId?: string // Timesheet ID for force clock out
  startDate?: Date // Normalized Date value
  endDate?: Date // Normalized Date value
  startTime?: string // HH:mm
  endTime?: string // HH:mm
  realStartTime?: string // HH:mm
  realEndTime?: string // HH:mm
  location: string
  role: string
  notes?: string
  cancelledReason?: CancelledReason
  status:
    | "planned"
    | "open"
    | "empty"
    | "in-progress"
    | "clocked-in"
    | "clocked-out"
    | "overdue"
    | "no-show"
    | "cancelled"
    | "unnotified-absence"
  lastEditedBy?: string
  lastEditedAt?: string
  vehicleId?: string | null
  vehicleReg?: string | null
  // Additional fields for filtering
  area?: string // Backend area format (e.g., "Rc1 Deliveries")
  type?: string // Sheriff type (e.g., "HivedVan")
  employer?: string // Employer (e.g., "Hived")
}

interface ShiftDetailsDialogProps {
  mode: "view" | "edit"
  shift: ShiftDetails | null
  open: boolean
  onOpenChange: (open: boolean) => void
  onSave?: (updated: ShiftDetails) => void
  onForceClockIn?: (shiftId: string) => void
  onForceClockOut?: (shiftId: string) => void
  // Only passed where removing the vehicle belongs, so the control stays hidden elsewhere.
  onUnassignVehicle?: (shiftId: string) => void
  isUnassigningVehicle?: boolean
  isSaving?: boolean
  // People data passed from parent
  people?: Person[]
  peopleLoading?: boolean
}

const ShiftDetailsDialog: React.FC<ShiftDetailsDialogProps> = ({
  mode,
  shift,
  open,
  onOpenChange,
  onSave,
  onForceClockIn,
  onForceClockOut,
  onUnassignVehicle,
  isUnassigningVehicle = false,
  isSaving = false,
  people = [],
  peopleLoading = false,
}) => {
  const { user } = useAuth()
  const isView = mode === "view"
  const isEditable =
    shift?.status === "planned" || shift?.status === "empty" || shift?.status === "open"
  const isDSP = user?.role === "DSP"
  const canViewAudit = user?.role === "Admin" || user?.role === "Manager"

  const [formData, setFormData] = React.useState<ShiftDetails | null>(shift)
  const { changesOpen: auditOpen, setChangesOpen: setAuditOpen } = useShiftChangesPanel({
    recordId: shift?.id,
    open,
  })

  // Check if shift has timesheet or if 1 hour has passed since start time
  const hasTimesheet = Boolean(shift?.timesheetId)
  const isEmptyShift = shift?.status === "empty"
  const oneHourPassed = React.useMemo(() => {
    if (!shift?.startDate || !shift.startTime) return false
    try {
      const startDateTime = ukInstantOf(shift.startDate, shift.startTime)
      if (isNaN(startDateTime.getTime())) return false
      const diff = differenceInHours(startDateTime, new Date())
      return diff < -1 // More than 1 hour has passed
    } catch {
      return false
    }
  }, [shift?.startDate, shift?.startTime])

  // For empty shifts: cannot be edited if 1 hour has passed (applies to all users)
  const isEmptyShiftLocked = isEmptyShift && oneHourPassed

  // For DSP users: person can only be updated if no timesheet and within 1 hour
  const canUpdatePerson = isDSP && !hasTimesheet && !oneHourPassed

  // For DSP users, all fields except person are always read-only
  // For empty shifts, all fields are read-only if 1 hour has passed
  const isFieldReadOnly = isDSP || isEmptyShiftLocked

  // Determine the message to show (only when there's a restriction)
  const errorMessage = React.useMemo(() => {
    if (isEmptyShiftLocked) {
      return "This block cannot be edited because more than 1 hour has passed since the start time."
    }
    if (!isDSP) return null
    if (hasTimesheet) {
      return "The person assignment cannot be updated because a timesheet has been started."
    }
    if (oneHourPassed) {
      return "The person assignment cannot be updated because more than 1 hour has passed since the start time."
    }
    return null // No message when person can be updated
  }, [isDSP, hasTimesheet, oneHourPassed, isEmptyShiftLocked])

  React.useEffect(() => {
    setFormData(shift)
  }, [shift])

  const normalizeText = (value?: string | null) => (value ?? "").trim()
  const normalizeDate = (value?: string | Date | null) => {
    if (!value) return ""
    const parsed = value instanceof Date ? value : new Date(value)
    if (Number.isNaN(parsed.getTime())) return typeof value === "string" ? value.slice(0, 10) : ""
    return parsed.toISOString().slice(0, 10)
  }

  const hasPendingUnsavedChanges = React.useMemo(() => {
    if (!shift || !formData) return false

    return (
      normalizeText(formData.personId) !== normalizeText(shift.personId) ||
      normalizeDate(formData.startDate) !== normalizeDate(shift.startDate) ||
      normalizeDate(formData.endDate) !== normalizeDate(shift.endDate) ||
      normalizeText(formData.startTime) !== normalizeText(shift.startTime) ||
      normalizeText(formData.endTime) !== normalizeText(shift.endTime) ||
      normalizeText(formData.location) !== normalizeText(shift.location) ||
      normalizeText(formData.notes) !== normalizeText(shift.notes) ||
      normalizeText(formData.cancelledReason) !== normalizeText(shift.cancelledReason)
    )
  }, [formData, shift])

  if (!formData) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Block Details</DialogTitle>
          </DialogHeader>
          <div className="text-sm text-palette-jungleGreen600">No block selected.</div>
        </DialogContent>
      </Dialog>
    )
  }

  const disableForceActionsUntilSave = hasPendingUnsavedChanges

  const shiftShouldHaveStartedSixHoursAgo = (() => {
    const startDateTime =
      formData.startDate && formData.startTime
        ? ukInstantOf(formData.startDate, formData.startTime)
        : undefined
    if (!startDateTime || isNaN(startDateTime.getTime())) {
      return false
    }
    const diff = differenceInHours(startDateTime, new Date())
    return diff < -6
  })()

  const forceClockInDisabled =
    !!formData.realStartTime || disableForceActionsUntilSave || shiftShouldHaveStartedSixHoursAgo
  const forceClockOutDisabled =
    !formData.realStartTime || !!formData.realEndTime || disableForceActionsUntilSave

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (isView || !isEditable) return onOpenChange(false)
    onSave?.(formData)
  }

  const handleForceClockInClick = () => {
    onForceClockIn?.(formData!.id)
  }

  const handleForceClockOutClick = () => {
    if (formData?.timesheetId) {
      onForceClockOut?.(formData.timesheetId)
    }
  }

  const handleSaveNotesClick = () => {
    if (!formData || isSaving) return
    onSave?.(formData)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(
          "w-[calc(100vw-2rem)] overflow-hidden p-0",
          auditOpen && canViewAudit ? "max-w-[1100px]" : "max-w-xl",
        )}
      >
        <DialogHeader className="border-b px-6 py-5 pr-20">
          <div className="flex items-center gap-3">
            <DialogTitle className="text-2xl font-bold">
              {isView ? "View Block" : "Edit Block"}
            </DialogTitle>
            {canViewAudit ? (
              <ShiftChangesButton
                open={auditOpen}
                onClick={() => setAuditOpen((current) => !current)}
              />
            ) : null}
          </div>
        </DialogHeader>
        <div className="relative flex h-[72vh] max-h-[760px] min-h-[420px]">
          <div className="min-w-0 flex-1 overflow-y-auto px-6 py-6">
            {isView ? (
              <div className="space-y-6">
                {/* Person - Full Width */}
                <div>
                  <Label>Person</Label>
                  <div className="mt-1 text-sm">{formData.name}</div>
                </div>

                {/* Start Date & End Date */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Start Date</Label>
                    <div className="mt-1 text-sm">
                      {formData.startDate ? format(new Date(formData.startDate), "PPP") : "-"}
                    </div>
                  </div>
                  <div>
                    <Label>End Date</Label>
                    <div className="mt-1 text-sm">
                      {formData.endDate ? format(new Date(formData.endDate), "PPP") : "-"}
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Start Time</Label>
                    <div className="mt-1 text-sm">{formData.startTime || "-"}</div>
                  </div>
                  <div>
                    <Label>End Time</Label>
                    <div className="mt-1 text-sm">{formData.endTime || "-"}</div>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Status</Label>
                    <div className="mt-1 text-sm capitalize">
                      {formData.status.replace("-", " ")}
                    </div>
                  </div>
                  <div>
                    <Label>Block Type</Label>
                    <div className="mt-1 text-sm">{formData.type}</div>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Location</Label>
                    <div className="mt-1 text-sm">{formData.location}</div>
                  </div>
                  <div>
                    <Label>Role</Label>
                    <div className="mt-1 text-sm">{formData.role}</div>
                  </div>
                </div>
                {onUnassignVehicle && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="md:col-span-2">
                      <Label>Vehicle</Label>
                      <div className="mt-1 text-sm">
                        {formData.vehicleReg || (
                          <span className="text-palette-jungleGreen500 italic">None</span>
                        )}
                      </div>
                    </div>
                  </div>
                )}
                <RoleTeamGuard
                  roles={["Admin", "Manager"]}
                  fallback={
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="md:col-span-2">
                        <Label>Block Notes</Label>
                        <div className="mt-1 text-sm whitespace-pre-wrap">
                          {formData.notes || ""}
                        </div>
                      </div>
                    </div>
                  }
                >
                  {!onSave ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="md:col-span-2">
                        <Label>Block Notes</Label>
                        <div className="mt-1 text-sm whitespace-pre-wrap">
                          {formData.notes || ""}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2 md:col-span-2">
                        <Label htmlFor="view-notes">Block Notes</Label>
                        <Textarea
                          id="view-notes"
                          placeholder="Add any notes about this block"
                          value={formData.notes || ""}
                          onChange={(e) =>
                            setFormData((p) => (p ? { ...p, notes: e.target.value } : p))
                          }
                          disabled={isSaving}
                        />
                      </div>
                    </div>
                  )}
                </RoleTeamGuard>

                {formData.status === "cancelled" || formData.status === "unnotified-absence" ? (
                  <div className="grid grid-cols-1 gap-4">
                    <div className="border rounded-md p-3 bg-red-50 border-red-200">
                      <div className="text-sm font-medium mb-1 text-red-800">
                        Cancellation Reason
                      </div>
                      <div className="text-sm text-red-700">
                        {formData.cancelledReason || "No reason provided"}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="border rounded-md p-3 bg-palette-jungleGreen000">
                      <div className="text-sm font-medium mb-1">Scheduled</div>
                      <div className="text-sm flex items-center gap-2">
                        <Clock className="h-4 w-4 text-palette-jungleGreen500" />
                        <span>
                          {(formData.startTime || "-") + " - " + (formData.endTime || "-")}
                          {(() => {
                            const mb = (start?: string, end?: string) => {
                              if (!start || !end) return 0
                              const [sh, sm] = start.split(":").map(Number)
                              const [eh, em] = end.split(":").map(Number)
                              if ([sh, sm, eh, em].some((n) => Number.isNaN(n))) return 0
                              const s = sh * 60 + sm
                              let e = eh * 60 + em
                              if (e < s) e += 24 * 60
                              return Math.max(0, e - s)
                            }
                            const mins = mb(formData.startTime, formData.endTime)
                            if (!mins) return ""
                            const h = Math.floor(mins / 60)
                            const m = mins % 60
                            return ` (${h}h${m ? ` ${m}m` : ""})`
                          })()}
                        </span>
                      </div>
                    </div>
                    <div className="border rounded-md p-3 bg-palette-jungleGreen000">
                      <div className="text-sm font-medium mb-1">Clocked</div>
                      <div className="text-sm flex items-center gap-2">
                        <Clock className="h-4 w-4 text-palette-jungleGreen500" />
                        <span>
                          {(formData.realStartTime || "-") + " - " + (formData.realEndTime || "-")}
                          {(() => {
                            const mb = (start?: string, end?: string) => {
                              if (!start || !end) return 0
                              const [sh, sm] = start.split(":").map(Number)
                              const [eh, em] = end.split(":").map(Number)
                              if ([sh, sm, eh, em].some((n) => Number.isNaN(n))) return 0
                              const s = sh * 60 + sm
                              let e = eh * 60 + em
                              if (e < s) e += 24 * 60
                              return Math.max(0, e - s)
                            }
                            const mins = mb(formData.realStartTime, formData.realEndTime)
                            if (!mins) return ""
                            const h = Math.floor(mins / 60)
                            const m = mins % 60
                            return ` (${h}h${m ? ` ${m}m` : ""})`
                          })()}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
                <RoleTeamGuard roles={["Admin", "Manager"]} fallback={null}>
                  {onForceClockIn &&
                    formData.status !== "cancelled" &&
                    formData.status !== "unnotified-absence" &&
                    formData.status !== "empty" &&
                    formData.personId && (
                      <div className="border rounded-md p-4 bg-blue-50 border-blue-200">
                        <div className="text-sm font-medium mb-3 text-blue-800">
                          Force Timesheet Actions
                        </div>
                        {disableForceActionsUntilSave && (
                          <div className="text-sm mb-3 text-amber-800">
                            Please save changes first before using force clock actions.
                          </div>
                        )}
                        {shiftShouldHaveStartedSixHoursAgo && !formData.realStartTime && (
                          <div className="text-sm mb-3 text-amber-800">
                            Force clock in is disabled because the block started more than 6 hours
                            ago.
                          </div>
                        )}
                        <div className="flex gap-3">
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={handleForceClockInClick}
                            disabled={forceClockInDisabled}
                            className={`flex-1 ${
                              forceClockInDisabled
                                ? "bg-palette-jungleGreen000 border-palette-jungleGreen200 text-palette-jungleGreen400 cursor-not-allowed"
                                : "bg-green-50 border-green-300 text-green-700 hover:bg-green-100"
                            }`}
                          >
                            <Play className="h-4 w-4 mr-2" />
                            Force Clock In
                          </Button>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={handleForceClockOutClick}
                            disabled={forceClockOutDisabled}
                            className={`flex-1 ${
                              forceClockOutDisabled
                                ? "bg-palette-jungleGreen000 border-palette-jungleGreen200 text-palette-jungleGreen400 cursor-not-allowed"
                                : "bg-red-50 border-red-300 text-red-700 hover:bg-red-100"
                            }`}
                          >
                            <Square className="h-4 w-4 mr-2" />
                            Force Clock Out
                          </Button>
                        </div>
                      </div>
                    )}
                </RoleTeamGuard>
                <div className="flex justify-between items-center pt-4 border-t">
                  <div className="text-xs text-palette-jungleGreen500">
                    {formData.lastEditedBy && formData.lastEditedAt ? (
                      <div>
                        <div>
                          Last Updated:{" "}
                          {new Date(formData.lastEditedAt)
                            .toLocaleDateString("en-GB")
                            .replace(/\//g, "-")}{" "}
                          {new Date(formData.lastEditedAt).toLocaleTimeString("en-GB", {
                            hour: "2-digit",
                            minute: "2-digit",
                            hour12: false,
                          })}
                        </div>
                        <div>Updated By: {formData.lastEditedBy}</div>
                      </div>
                    ) : (
                      <>{!canViewAudit ? "No edit history available" : null}</>
                    )}
                  </div>
                  <div className="flex gap-3">
                    <RoleTeamGuard roles={["Admin", "Manager"]} fallback={null}>
                      {onUnassignVehicle && formData.vehicleId && (
                        <Button
                          type="button"
                          variant="outline"
                          disabled={isUnassigningVehicle}
                          onClick={() => onUnassignVehicle(formData.id)}
                          className="border-red-300 bg-red-50 text-red-700 hover:bg-red-100"
                        >
                          {isUnassigningVehicle ? (
                            <>
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                              Removing...
                            </>
                          ) : (
                            "Remove vehicle"
                          )}
                        </Button>
                      )}
                      {onSave && (
                        <Button type="button" onClick={handleSaveNotesClick} disabled={isSaving}>
                          {isSaving ? (
                            <>
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                              Saving...
                            </>
                          ) : (
                            "Save Notes"
                          )}
                        </Button>
                      )}
                    </RoleTeamGuard>
                    <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                      Close
                    </Button>
                  </div>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Error Message */}
                {errorMessage && (
                  <div className="border rounded-md p-4 bg-red-50 border-red-200">
                    <div className="text-sm font-medium text-red-800">{errorMessage}</div>
                  </div>
                )}
                {/* Person - Full Width */}
                <div className="space-y-2">
                  <Label htmlFor="person">Person</Label>
                  <PersonCombobox
                    value={formData.personId || ""}
                    onValueChange={(personId, person) => {
                      setFormData((p) =>
                        p
                          ? {
                              ...p,
                              personId: personId,
                              name: person ? `${person.first_name} ${person.surname}` : p.name,
                            }
                          : p,
                      )
                    }}
                    disabled={isView || (isDSP && !canUpdatePerson) || isEmptyShiftLocked}
                    placeholder="Select a person..."
                    area="all"
                    location="all"
                    people={people}
                    isLoading={peopleLoading}
                    sheriffType="all"
                    employer="all"
                  />
                </div>

                {/* Start Date & End Date */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Start Date</Label>
                    {isFieldReadOnly ? (
                      <div className="mt-1 text-sm px-3 py-2 border rounded-md bg-palette-jungleGreen000">
                        {formData.startDate ? format(new Date(formData.startDate), "PPP") : "-"}
                      </div>
                    ) : (
                      <Popover modal={true}>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            className={cn(
                              "w-full justify-start text-left font-normal",
                              !formData.startDate && "text-muted-foreground",
                            )}
                            disabled={isView || isFieldReadOnly}
                          >
                            <Calendar className="mr-2 h-4 w-4" />
                            {formData.startDate ? (
                              format(new Date(formData.startDate), "PPP")
                            ) : (
                              <span>Pick a date</span>
                            )}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0 z-[100]" align="start" sideOffset={4}>
                          <CalendarComponent
                            mode="single"
                            selected={formData.startDate ? new Date(formData.startDate) : undefined}
                            onSelect={(date) =>
                              date && setFormData((p) => (p ? { ...p, startDate: date } : p))
                            }
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label>End Date</Label>
                    {isFieldReadOnly ? (
                      <div className="mt-1 text-sm px-3 py-2 border rounded-md bg-palette-jungleGreen000">
                        {formData.endDate ? format(new Date(formData.endDate), "PPP") : "-"}
                      </div>
                    ) : (
                      <Popover modal={true}>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            className={cn(
                              "w-full justify-start text-left font-normal",
                              !formData.endDate && "text-muted-foreground",
                            )}
                            disabled={isView || isFieldReadOnly}
                          >
                            <Calendar className="mr-2 h-4 w-4" />
                            {formData.endDate ? (
                              format(new Date(formData.endDate), "PPP")
                            ) : (
                              <span>Pick a date</span>
                            )}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0 z-[100]" align="start" sideOffset={4}>
                          <CalendarComponent
                            mode="single"
                            selected={formData.endDate ? new Date(formData.endDate) : undefined}
                            onSelect={(date) =>
                              date && setFormData((p) => (p ? { ...p, endDate: date } : p))
                            }
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>
                    )}
                  </div>
                </div>
                {/* Start Time & End Time */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="startTime">Start Time</Label>
                    {isFieldReadOnly ? (
                      <div className="mt-1 text-sm px-3 py-2 border rounded-md bg-palette-jungleGreen000">
                        {formData.startTime || "-"}
                      </div>
                    ) : (
                      <div className="relative">
                        <Input
                          id="startTime"
                          type="time"
                          value={formData.startTime || ""}
                          onChange={(e) =>
                            setFormData((p) => (p ? { ...p, startTime: e.target.value } : p))
                          }
                          disabled={isView || isFieldReadOnly}
                          required
                          className="pl-10 [&::-webkit-calendar-picker-indicator]:opacity-0 [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:inset-0 [&::-webkit-calendar-picker-indicator]:w-full [&::-webkit-calendar-picker-indicator]:h-full [&::-webkit-calendar-picker-indicator]:cursor-pointer"
                        />
                        <Clock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-black pointer-events-none" />
                      </div>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="endTime">End Time</Label>
                    {isFieldReadOnly ? (
                      <div className="mt-1 text-sm px-3 py-2 border rounded-md bg-palette-jungleGreen000">
                        {formData.endTime || "-"}
                      </div>
                    ) : (
                      <div className="relative">
                        <Input
                          id="endTime"
                          type="time"
                          value={formData.endTime || ""}
                          onChange={(e) =>
                            setFormData((p) => (p ? { ...p, endTime: e.target.value } : p))
                          }
                          disabled={isView || isFieldReadOnly}
                          required
                          className="pl-10 [&::-webkit-calendar-picker-indicator]:opacity-0 [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:inset-0 [&::-webkit-calendar-picker-indicator]:w-full [&::-webkit-calendar-picker-indicator]:h-full [&::-webkit-calendar-picker-indicator]:cursor-pointer"
                        />
                        <Clock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-black pointer-events-none" />
                      </div>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Status</Label>
                    <div className="text-sm capitalize">{formData.status.replace("-", " ")}</div>
                  </div>
                  <div className="space-y-2">
                    <Label>Block Type</Label>
                    <div className="text-sm">{formData.type}</div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Location</Label>
                    <div className="text-sm">{formData.location}</div>
                  </div>
                  <div className="space-y-2">
                    <Label>Role</Label>
                    <div className="text-sm">{formData.role}</div>
                  </div>
                </div>

                {/* Notes - Hidden for DSP */}
                <RoleTeamGuard roles={["Admin", "Manager"]} fallback={null}>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2 md:col-span-2">
                      <Label htmlFor="notes">Block Notes</Label>
                      <Textarea
                        id="notes"
                        placeholder="Add any notes about this block"
                        value={formData.notes || ""}
                        onChange={(e) =>
                          setFormData((p) => (p ? { ...p, notes: e.target.value } : p))
                        }
                      />
                    </div>
                  </div>
                </RoleTeamGuard>

                {/* Force Timesheet Actions - Edit Mode - Hidden for DSP */}
                <RoleTeamGuard roles={["Admin", "Manager"]} fallback={null}>
                  {onForceClockIn &&
                    formData.status !== "cancelled" &&
                    formData.status !== "unnotified-absence" &&
                    formData.status !== "empty" &&
                    formData.personId && (
                      <div className="border rounded-md p-4 bg-blue-50 border-blue-200">
                        <div className="text-sm font-medium mb-3 text-blue-800">
                          Force Timesheet Actions
                        </div>
                        {disableForceActionsUntilSave && (
                          <div className="text-sm mb-3 text-amber-800">
                            Please save changes first before using force clock actions.
                          </div>
                        )}
                        {shiftShouldHaveStartedSixHoursAgo && !formData.realStartTime && (
                          <div className="text-sm mb-3 text-amber-800">
                            Force clock in is disabled because the block started more than 6 hours
                            ago.
                          </div>
                        )}
                        <div className="flex gap-3">
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={handleForceClockInClick}
                            disabled={forceClockInDisabled}
                            className={`flex-1 ${
                              forceClockInDisabled
                                ? "bg-palette-jungleGreen000 border-palette-jungleGreen200 text-palette-jungleGreen400 cursor-not-allowed"
                                : "bg-green-50 border-green-300 text-green-700 hover:bg-green-100"
                            }`}
                          >
                            <Play className="h-4 w-4 mr-2" />
                            Force Clock In
                          </Button>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={handleForceClockOutClick}
                            disabled={forceClockOutDisabled}
                            className={`flex-1 ${
                              forceClockOutDisabled
                                ? "bg-palette-jungleGreen000 border-palette-jungleGreen200 text-palette-jungleGreen400 cursor-not-allowed"
                                : "bg-red-50 border-red-300 text-red-700 hover:bg-red-100"
                            }`}
                          >
                            <Square className="h-4 w-4 mr-2" />
                            Force Clock Out
                          </Button>
                        </div>
                      </div>
                    )}
                </RoleTeamGuard>

                {/* Cancel Block - Hidden for DSP */}
                <RoleTeamGuard roles={["Admin", "Manager"]} fallback={null}>
                  <div className="space-y-2">
                    <Label htmlFor="cancel" className="text-red-600">
                      Cancel Block
                    </Label>
                    <Select
                      value={formData.cancelledReason || ""}
                      onValueChange={(value) =>
                        setFormData((p) =>
                          p ? { ...p, cancelledReason: value as CancelledReason } : p,
                        )
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select a reason" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Sickness">Sickness</SelectItem>
                        <SelectItem value="FamilyEmergency">Family Emergency</SelectItem>
                        <SelectItem value="OtherWorkCommitments">Other Work Commitments</SelectItem>
                        <SelectItem value="HivedCapacity">Hived Capacity</SelectItem>
                        <SelectItem value="RetailerRequest">Retailer Request</SelectItem>
                        <SelectItem value="Offboarded">Offboarded</SelectItem>
                        <SelectItem value="RequestedByDriver">Requested By Driver</SelectItem>
                        <SelectItem value="RequestedByDsp">Requested by DSP</SelectItem>
                        <SelectItem value="UnavailabilityRequest">
                          Unavailability Request
                        </SelectItem>
                        <SelectItem value="MovedPosition">Moved Position</SelectItem>
                        <SelectItem value="Onboarded">Onboarded</SelectItem>
                        <SelectItem value="SheriffTypeChanged">Sheriff Type Changed</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </RoleTeamGuard>

                <div className="flex justify-between items-center pt-4 border-t">
                  <div className="text-xs text-palette-jungleGreen500">
                    {formData.lastEditedBy && formData.lastEditedAt ? (
                      <div>
                        <div>
                          Last Updated:{" "}
                          {new Date(formData.lastEditedAt)
                            .toLocaleDateString("en-GB")
                            .replace(/\//g, "-")}{" "}
                          {new Date(formData.lastEditedAt).toLocaleTimeString("en-GB", {
                            hour: "2-digit",
                            minute: "2-digit",
                            hour12: false,
                          })}
                        </div>
                        <div>Updated By: {formData.lastEditedBy}</div>
                      </div>
                    ) : (
                      <>{!canViewAudit ? "No edit history available" : null}</>
                    )}
                  </div>
                  <div className="flex gap-3">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => onOpenChange(false)}
                      disabled={isSaving}
                    >
                      {onSave ? "Cancel" : "Close"}
                    </Button>
                    {onSave && (
                      <Button
                        type="submit"
                        disabled={isSaving || (isDSP && !canUpdatePerson) || isEmptyShiftLocked}
                      >
                        {isSaving ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Saving...
                          </>
                        ) : (
                          "Save Changes"
                        )}
                      </Button>
                    )}
                  </div>
                </div>
              </form>
            )}
          </div>
          <ShiftAuditDrawer
            shiftId={formData.id}
            timesheetId={formData.timesheetId}
            open={auditOpen}
            canView={canViewAudit}
            onClose={() => setAuditOpen(false)}
          />
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default ShiftDetailsDialog
